/**
 * Fetch wrapper with timeout and error handling utilities
 */

import { getAuthHeaders, isTokenExpired, getAuthToken, refreshAccessToken } from './auth';

const DEFAULT_TIMEOUT = 30000; // 30 seconds

/**
 * Handle 402 Payment Required errors by dispatching tierLimitExceeded event
 * This triggers the global ActionBlockedModal
 */
const handle402Error = async (response, url) => {
  try {
    const data = await response.clone().json();
    if (data?.errorCode === 'OVER_VEHICLE_LIMIT' || data?.upgradeRequired) {
      // Extract action from URL for better context
      let blockedAction = 'perform this action';
      if (url.includes('/analytics')) blockedAction = 'view analytics';
      else if (url.includes('/cohosts')) blockedAction = 'manage co-hosts';
      else if (url.includes('/expenses')) blockedAction = 'add expense';
      else if (url.includes('/maintenance')) blockedAction = 'add maintenance record';
      else if (url.includes('/team')) blockedAction = 'manage team';
      
      window.dispatchEvent(new CustomEvent('tierLimitExceeded', {
        detail: {
          currentVehicles: data.currentVehicles,
          vehicleLimit: data.vehicleLimit,
          currentTier: data.currentTier,
          suggestedTier: data.suggestedTier,
          currentTeamMembers: data.currentTeamMembers,
          teamLimit: data.teamLimit,
          suggestedLimits: data.suggestedLimits,
          message: data.message,
          blockedAction,
        }
      }));
    }
  } catch {
    // Ignore JSON parse errors
  }
};

/**
 * Fetch with timeout - automatically aborts requests that take too long
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options (headers, method, body, etc.)
 * @param {number} timeout - Timeout in milliseconds (default: 30000)
 * @returns {Promise<Response>} The fetch response
 */
export const fetchWithTimeout = async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
  const controller = new AbortController();
  const { signal: externalSignal, ...rest } = options;

  // If caller provided a signal, mirror its abort onto our controller so timeout still works
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...rest,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    
    // Handle 402 Payment Required errors globally
    if (response.status === 402) {
      handle402Error(response, url);
    }
    
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      // Check if it was a timeout or manual abort
      if (!options.signal?.aborted) {
        throw new Error(`Request timeout after ${timeout}ms`);
      }
    }
    throw error;
  }
};

/**
 * Fetch JSON with timeout and automatic JSON parsing
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<{data: any, response: Response}>}
 */
export const fetchJSON = async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
  const response = await fetchWithTimeout(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  }, timeout);

  if (!response.ok) {
    let errorMessage = `HTTP Error ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // Could not parse error response as JSON
    }
    const error = new Error(errorMessage);
    error.status = response.status;
    error.response = response;
    throw error;
  }

  const data = await response.json();
  return { data, response };
};

/**
 * Authenticated fetch with timeout
 * Automatically adds Authorization header from localStorage
 * BUG-010 FIX: Now checks token expiry and refreshes before making the request
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>}
 */
export const authFetch = async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
  // BUG-010: Check if token needs refresh before making the request
  const token = getAuthToken();
  if (token && isTokenExpired(token)) {
    await refreshAccessToken();
  }
  
  const response = await fetchWithTimeout(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  }, timeout);
  
  // If we still get 401, try one more refresh and retry
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return fetchWithTimeout(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      }, timeout);
    }
  }
  
  return response;
};

/**
 * Authenticated JSON fetch with timeout
 * BUG-010 FIX: Now checks token expiry and refreshes before making the request
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<{data: any, response: Response}>}
 */
export const authFetchJSON = async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
  // BUG-010: Check if token needs refresh before making the request
  const token = getAuthToken();
  if (token && isTokenExpired(token)) {
    await refreshAccessToken();
  }
  
  return fetchJSON(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  }, timeout);
};

/**
 * Check if error is a timeout error
 * @param {Error} error 
 * @returns {boolean}
 */
export const isTimeoutError = (error) => {
  return error.message?.includes('timeout') || error.name === 'AbortError';
};

/**
 * Check if error is a network error
 * @param {Error} error 
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  return error.message?.includes('Network') || 
         error.message?.includes('Failed to fetch') ||
         error.name === 'TypeError';
};

export default {
  fetchWithTimeout,
  fetchJSON,
  authFetch,
  authFetchJSON,
  isTimeoutError,
  isNetworkError,
  DEFAULT_TIMEOUT,
};
